import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, vModelText as _vModelText, withKeys as _withKeys, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex-1 flex items-center relative" }
const _hoisted_2 = { class: "chats-container flex-1 overflow-y-auto" }
const _hoisted_3 = {
  key: 0,
  class: "empty-search-results flex flex-col items-center justify-center h-full p-6 text-center"
}
const _hoisted_4 = {
  key: 1,
  class: "empty-chats flex flex-col items-center justify-center h-full p-6 text-center"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "avatar-container relative" }
const _hoisted_7 = { class: "chat-info flex-1 ml-3 overflow-hidden" }
const _hoisted_8 = { class: "flex justify-between items-center" }
const _hoisted_9 = { class: "text-white font-medium truncate" }
const _hoisted_10 = { class: "flex justify-between items-center mt-1" }
const _hoisted_11 = { class: "flex items-center max-w-[85%] text-xs text-zinc-400" }
const _hoisted_12 = { style: {"display":"-webkit-box","-webkit-line-clamp":"2","-webkit-box-orient":"vertical","overflow":"hidden","text-overflow":"ellipsis"} }
const _hoisted_13 = { class: "text-white text-xs font-medium" }
const _hoisted_14 = { class: "text-center" }

import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { 
  Menu, 
  Search,
  SearchX,
  X,
  CheckCircle2, 
  Check, 
  CheckCheck, 
  Video, 
  Image as ImageIcon, 
  Volume2, 
  Pin, 
  Users, 
  MessageCircle,
  MessageSquare,
  Settings,
  ArrowLeft,
  Send
} from 'lucide-vue-next'
import TelegramMessagePreview from '@/components/ui/telegram/TelegramMessagePreview.vue'
import { useChat } from '@/composables/useChat'
import { Chat } from '@/interfaces/chats/Chat'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import { ChatMessageSender } from '@/enums/chats/messages/ChatMessageSender'
import { ChatMessageTypeEnum } from '@/enums/chats/messages/ChatMessageTypeEnum'
import TelegramChatSkeleton from '@/components/ui/telegram/TelegramChatSkeleton.vue'
import TelegramChatsSkeleton from '@/components/ui/telegram/TelegramChatsSkeleton.vue'
import { useWebSocket } from '@/composables/useWebSocket'
import Pagination from '@/components/ui/pagination/Pagination.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'ChatsView',
  setup(__props) {

_useCssVars(_ctx => ({
  "64a1fcf9": (currentTheme.value === "telegram" ? "#17212b" : "#111b21"),
  "6409a825": (currentTheme.value === "telegram" ? "#232e3c" : "#222e35")
}))

const route = useRoute()
const webSocket = computed(() => useWebSocket({ page: 1 }))
const chats = ref<IPagination<Chat>>()
const isChatLoading = ref(false)
const { getAll: getAllChats, getByUuid: getChatByUuid, read: readMessages, send: sendMessage } = useChat()
const socket = ref<typeof webSocket.value>()
const page = ref<string>('10')
// Selected chat state
const selectedChat = ref<Chat>()
// Search state
const searchQuery = ref('')
const isSearchActive = ref(false)
// Theme state (telegram or whatsapp)
const currentTheme = ref('telegram')
// Responsive state
const isMobileView = ref(false)

// Set theme function
// const setTheme = (theme: 'telegram' | 'whatsapp') => {
//   currentTheme.value = theme
// }

// Function to check if the view is mobile
const checkMobileView = () => {
  isMobileView.value = window.innerWidth < 768
}

// Function to select a chat
const selectChat = async (index: number) => {
  const chatInList = chats.value!.data[index]
  if (chatInList.unreadMessagesCount! > 0) {
    readMessages(chatInList.id)
  }
  isChatLoading.value = true
  const chat = await getChatByUuid(chatInList!.uuid)
  chats.value!.data[index].unreadMessagesCount = 0
  selectedChat.value = { ...chat, lead: chatInList!.lead }
  isChatLoading.value = false
}

// Helper functions
const getInitials = (name: string): string => {
  if (!name) return 'U'
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
}

const getAvatarColorClass = (name: string): string => {
  // Generate a consistent color based on the name
  const colors = [
    'bg-[#e17076]', // red
    'bg-[#7bc862]', // green
    'bg-[#65aadd]', // blue
    'bg-[#a695e7]', // purple
    'bg-[#ee7aae]', // pink
    'bg-[#6ec9cb]', // teal
    'bg-[#faa774]'  // orange
  ]
  
  // Simple hash function to get a consistent index
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  const index = Math.abs(hash) % colors.length
  return colors[index]
}

// Function to get messages for the chat preview component
const getChatMessages = (chat: Chat) => {
  return chat.messages?.map((msg) => {
    let text = msg.content
    let type: any = msg.type
    let copyable = false
    let isRedirectLink = false

    if (msg.type === ChatMessageTypeEnum.URL_BUTTON || msg.type === ChatMessageTypeEnum.ACTION_BUTTON) {
      type = 'button'
    }

    if (msg.type === ChatMessageTypeEnum.URL_BUTTON) {
      isRedirectLink = true
    }

    if (msg.type === ChatMessageTypeEnum.COPYABLE_TEXT) {
      type = 'text'
      copyable = true
    }

    if (msg.type === ChatMessageTypeEnum.QR_CODE) {
      type = 'image'
      text = '/assets/bot/pix/qr-code-example.jpg'
    }

    if (msg.type === ChatMessageTypeEnum.DOCUMENT) {
      type = 'document'
    }

    if (msg.type === ChatMessageTypeEnum.LOCATION) {
      type = 'text'
      text = '📍 Localização enviada'
    }

    return { text, source: msg.type === ChatMessageTypeEnum.IMAGE || msg.type === ChatMessageTypeEnum.VIDEO || msg.type === ChatMessageTypeEnum.DOCUMENT ? msg.content : undefined, type, isFromAuthUser: msg.sender === ChatMessageSender.BOT, copyable, sentAt: msg.createdAt, isRedirectLink }
  }) || [
    { type: 'text' as const, text: chat.lastMessage }
  ]
}

async function handleSendMessage(content: string) {
  // selectedChat.value?.messages?.push({
  //   content,
  //   type: ChatMessageTypeEnum.TEXT,
  //   sender: ChatMessageSender.BOT,
  //   createdAt: new Date()
  // })
  await sendMessage(selectedChat.value!.uuid, { content, type: ChatMessageTypeEnum.TEXT })
}

function reloadAndGetAll({ page, search }: { page: string, search?: string }) {
  socket.value?.emit('paginate_chats', page)
  isSearchActive.value = !!search && search.trim() !== ''
  
  getAllChats({ page, search })
    .then((data) => chats.value = data)
}

function clearSearch() {
  searchQuery.value = ''
  isSearchActive.value = false
  reloadAndGetAll({ page: '1' })
}

watch(() => selectedChat.value, () => {
  nextTick(() => {
      if (selectedChat.value && window.innerWidth < 768) {
        document.querySelector('.mobile-bottom-navigation')!.classList.add('hidden')
      } else {
        document.querySelector('.mobile-bottom-navigation')!.classList.remove('hidden')
      }
    })
})

onMounted(() => {
  const initialSearch = route.query.search as string | undefined
  if (initialSearch) {
    searchQuery.value = initialSearch
    isSearchActive.value = true
    getAllChats({ search: initialSearch }).then((data) => chats.value = data)
  } else {
    getAllChats().then((data) => chats.value = data)
  }

  // socket must be initialized regardless of initial search
  socket.value = webSocket.value

  socket.value.on("new_message", (data) => {
    console.log(data,'nova mensagem')
    const chatIndex = chats.value?.data.findIndex(chat => chat.id === data.chat.id) 

    if (selectedChat.value && data.chat.id == selectedChat?.value?.id) {
      // inserir a mensagem na variavel
      selectedChat.value.messages?.push(data)

      readMessages(selectedChat.value.id)

      if (chatIndex !== undefined && chatIndex >= 0) {
        chats.value!.data[chatIndex].unreadMessagesCount = 0
      }

      // descer para a última mensagem
      nextTick(() => {
        const chatContainer = document.querySelector('#messages-container');
        if (chatContainer) {
          // Move o scroll para o final
          chatContainer.scrollTop = chatContainer.scrollHeight;
        }
      });
    } else if (chatIndex !== undefined && chatIndex >= 0) {
      chats.value!.data[chatIndex].unreadMessagesCount! += 1
    }

    if (chatIndex !== undefined && chatIndex >= 0) {
      chats.value!.data[chatIndex].lastMessage = data.content
    }
  });

  socket.value.on('new_chat', (chat) => {
    chats.value?.data.push(chat)
  })

  // Check if mobile view on mount
  checkMobileView()
  
  // Add resize event listener
  window.addEventListener('resize', checkMobileView)
})

onUnmounted(() => {
  if (socket.value) {
    socket.value.close()
  }
  
  // Remove resize event listener
  window.removeEventListener('resize', checkMobileView)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["chat-interface flex flex-col md:flex-row h-[87dvh]", currentTheme.value === 'telegram' ? 'bg-[#17212b]' : 'bg-[#111b21]'])
  }, [
    (chats.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["chat-list-container w-full md:w-[360px] flex-shrink-0 border-r flex flex-col", [
        currentTheme.value === 'telegram' ? 'border-[#232e3c]' : 'border-[#222e35]',
        (selectedChat.value && isMobileView.value) ? 'hidden md:flex' : 'flex'
      ]])
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["header p-3 flex items-center justify-between border-b", currentTheme.value === 'telegram' ? 'bg-[#17212b] border-[#232e3c]' : 'bg-[#202c33] border-[#222e35]'])
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(_unref(Search), {
                class: _normalizeClass([currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]', "size-4 mr-2 absolute left-3"])
              }, null, 8, ["class"]),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                onKeydown: _cache[1] || (_cache[1] = _withKeys(($event: any) => (reloadAndGetAll({ page: '1', search: searchQuery.value })), ["enter"])),
                class: _normalizeClass(["ps-10 search-bar rounded-md text-sm py-2 px-4 w-full focus:outline-none", currentTheme.value === 'telegram' ? 'bg-[#232e3c] placeholder:text-[#8a9aa9]' : 'bg-[#222e35] placeholder:text-[#aebac1]']),
                placeholder: "Pesquisar"
              }, null, 34), [
                [_vModelText, searchQuery.value]
              ]),
              (searchQuery.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    onClick: clearSearch,
                    class: _normalizeClass(["absolute right-2 hover:text-white", currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]'])
                  }, [
                    _createVNode(_unref(X), { class: "size-4" })
                  ], 2))
                : _createCommentVNode("", true)
            ])
          ], 2),
          _createElementVNode("div", _hoisted_2, [
            (chats.value.data.length === 0 && isSearchActive.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["empty-search-icon rounded-full p-5 mb-4", currentTheme.value === 'telegram' ? 'bg-[#232e3c]' : 'bg-[#222e35]'])
                  }, [
                    _createVNode(_unref(SearchX), {
                      class: _normalizeClass([currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]', "size-10"])
                    }, null, 8, ["class"])
                  ], 2),
                  _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "text-white text-lg font-medium mb-2" }, "Nenhum chat encontrado", -1)),
                  _createElementVNode("p", {
                    class: _normalizeClass([currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]', "text-sm mb-4"])
                  }, " Não encontramos nenhum resultado para \"" + _toDisplayString(searchQuery.value) + "\" ", 3),
                  _createElementVNode("button", {
                    onClick: clearSearch,
                    class: _normalizeClass(["text-white py-2 px-4 rounded-md text-sm transition-colors", currentTheme.value === 'telegram' ? 'bg-[#2b5278] hover:bg-[#3a6ea5]' : 'bg-[#00a884] hover:bg-[#029670]'])
                  }, " Limpar pesquisa ", 2)
                ]))
              : (chats.value.data.length === 0 && !isSearchActive.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["empty-chats-icon rounded-full p-5 mb-4", currentTheme.value === 'telegram' ? 'bg-[#232e3c]' : 'bg-[#222e35]'])
                    }, [
                      _createVNode(_unref(MessageSquare), {
                        class: _normalizeClass([currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]', "size-10"])
                      }, null, 8, ["class"])
                    ], 2),
                    _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "text-white text-lg font-medium mb-2" }, "Nenhum chat disponível", -1)),
                    _createElementVNode("p", {
                      class: _normalizeClass([currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]', "text-sm"])
                    }, " Você ainda não possui conversas ", 2)
                  ]))
                : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(chats.value.data, (chat, index) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: index,
                      class: _normalizeClass(["chat-item flex items-center p-3 cursor-pointer border-b last:border-b-0", [
              currentTheme.value === 'telegram' 
                ? [
                    'border-[#232e3c]', 
                    selectedChat.value?.id === chat.id ? 'bg-[#2b5278]' : 'hover:bg-[#232e3c]'
                  ] 
                : [
                    'border-[#222e35]', 
                    selectedChat.value?.id === chat.id ? 'bg-[#2a3942]' : 'hover:bg-[#222e35]'
                  ]
            ]]),
                      onClick: ($event: any) => (selectedChat.value?.id !== chat.id && selectChat(index))
                    }, [
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("div", {
                          class: _normalizeClass(["avatar rounded-full size-12 flex items-center justify-center text-white font-bold", getAvatarColorClass(chat.lead.firstName)])
                        }, _toDisplayString(getInitials(chat.lead.firstName)), 3)
                      ]),
                      _createElementVNode("div", _hoisted_7, [
                        _createElementVNode("div", _hoisted_8, [
                          _createElementVNode("h3", _hoisted_9, _toDisplayString(chat.lead.firstName), 1),
                          (chat.updatedAt)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 0,
                                class: _normalizeClass([currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]', "text-xs whitespace-nowrap"])
                              }, _toDisplayString(new Date(chat.updatedAt).toLocaleString()), 3))
                            : _createCommentVNode("", true)
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("p", _hoisted_12, _toDisplayString(chat.lastMessage), 1)
                          ]),
                          (chat.unreadMessagesCount)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["rounded-full size-6 flex items-center justify-center flex-shrink-0", currentTheme.value === 'telegram' ? 'bg-[#64b3f4]' : 'bg-[#00a884]'])
                              }, [
                                _createElementVNode("span", _hoisted_13, _toDisplayString(chat.unreadMessagesCount), 1)
                              ], 2))
                            : _createCommentVNode("", true)
                        ])
                      ])
                    ], 10, _hoisted_5))
                  }), 128))
          ]),
          _createElementVNode("div", {
            class: _normalizeClass(["bottom-nav border-t p-3 flex justify-between items-center", currentTheme.value === 'telegram' ? 'bg-[#17212b] border-[#232e3c]' : 'bg-[#202c33] border-[#222e35]'])
          }, [
            (chats.value?.pagination && chats.value.data.length > 0)
              ? (_openBlock(), _createBlock(Pagination, {
                  key: 0,
                  class: "mt-0",
                  "onUpdate:page": _cache[2] || (_cache[2] = page => reloadAndGetAll({ page, search: isSearchActive.value ? searchQuery.value : undefined })),
                  data: chats.value?.pagination
                }, null, 8, ["data"]))
              : _createCommentVNode("", true)
          ], 2)
        ], 2))
      : (_openBlock(), _createBlock(TelegramChatsSkeleton, { key: 1 })),
    _createElementVNode("div", {
      class: _normalizeClass(["chat-preview-container flex-1", (!selectedChat.value && isMobileView.value) ? 'hidden md:block' : 'block'])
    }, [
      (!isChatLoading.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (selectedChat.value)
              ? (_openBlock(), _createBlock(TelegramMessagePreview, {
                  key: 0,
                  class: "min-h-full",
                  onGoBack: _cache[3] || (_cache[3] = ($event: any) => (selectedChat.value = undefined)),
                  sender: selectedChat.value.lead.firstName,
                  messages: getChatMessages(selectedChat.value),
                  sentAt: selectedChat.value.updatedAt,
                  showAvatar: true,
                  onSendMessage: handleSendMessage,
                  disableMic: true,
                  disableAttachments: true,
                  theme: currentTheme.value
                }, null, 8, ["sender", "messages", "sentAt", "theme"]))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass(["h-full flex items-center justify-center", currentTheme.value === 'telegram' ? 'text-[#8a9aa9]' : 'text-[#aebac1]'])
                }, [
                  _createElementVNode("div", _hoisted_14, [
                    _createVNode(_unref(MessageCircle), { class: "size-16 mx-auto mb-4 opacity-30" }),
                    _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-lg" }, "Selecione um chat para ver a conversa", -1))
                  ])
                ], 2))
          ], 64))
        : _createCommentVNode("", true),
      (isChatLoading.value)
        ? (_openBlock(), _createBlock(TelegramChatSkeleton, {
            key: 1,
            sender: 'Lead',
            theme: currentTheme.value
          }, null, 8, ["theme"]))
        : _createCommentVNode("", true)
    ], 2)
  ], 2))
}
}

})